import { differenceInMinutes, parseISO } from 'date-fns';
import { useCallback, useRef } from 'react';

import type { DelayedExperimentStage, RecordExperimentAllocation } from '@growtherapy/analytics/client';

import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { useRecordExperimentAllocation } from './useRecordExperimentAllocation';

const NEW_ALLOCATION_WINDOW_MINUTES = 2;

function isNewAllocation(allocatedAt: string): boolean {
  const allocationTime = parseISO(allocatedAt);
  if (Number.isNaN(allocationTime.getTime())) {
    return false;
  }
  return Math.abs(differenceInMinutes(new Date(), allocationTime)) <= NEW_ALLOCATION_WINDOW_MINUTES;
}

export function useIntakeSummaryExperimentTracking(providerShortId?: string): {
  recordExperimentAllocation: RecordExperimentAllocation;
  onStageTransition: (stage: DelayedExperimentStage) => void;
} {
  const persistExperimentAllocation = useRecordExperimentAllocation();
  const { formSubmitted, userViewed } = useTrackEvent();
  const hasHandledAllocationRef = useRef(false);

  const recordExperimentAllocation = useCallback<RecordExperimentAllocation>(
    async (input) => {
      const allocation = await persistExperimentAllocation(input);
      const isFirstAllocationThisSession = !hasHandledAllocationRef.current;
      hasHandledAllocationRef.current = true;

      if (providerShortId && isFirstAllocationThisSession && isNewAllocation(allocation.allocatedAt)) {
        formSubmitted(EventTypeEnum.PROVIDER_INTAKE_SUMMARY_ALLOCATION_RECORDED, {
          startedAt: allocation.allocatedAt,
          experimentGroup: allocation.allocationValue,
        });
      }

      return allocation;
    },
    [formSubmitted, persistExperimentAllocation, providerShortId],
  );

  const onStageTransition = useCallback(
    (stage: DelayedExperimentStage): void => {
      if (!providerShortId) return;

      userViewed(EventTypeEnum.PROVIDER_INTAKE_SUMMARY_EXPERIMENT_STAGE_VIEWED, {
        status: stage.id,
      });
    },
    [providerShortId, userViewed],
  );

  return {
    recordExperimentAllocation,
    onStageTransition,
  };
}
